import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Button,
  EmptyState,
  Input,
  Numeric,
  OrderRow,
  Select,
  StatusPill,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
  type Density,
} from "@wmart/ui";
import { api, ApiError } from "../lib/api-client.js";
import { formatGBP } from "../lib/format.js";
import { orderStatusTone } from "../lib/cod-helpers.js";
import { useToast } from "../lib/toast.js";
import type {
  AdminOrderListResponse,
  AdminOrderSummaryDTO,
  OrderStatus,
} from "@wmart/shared-types";

const ORDER_STATUS_VALUES: OrderStatus[] = [
  "pending_payment",
  "placed",
  "out_for_delivery",
  "paid",
  "fulfilled",
  "cancelled",
  "partially_refunded",
  "refunded",
];

const STATUS_OPTIONS: Array<{ value: OrderStatus | "all"; label: string }> = [
  { value: "all", label: "All statuses" },
  { value: "pending_payment", label: "Pending payment" },
  { value: "placed", label: "Placed" },
  // P17 — COD interstitial; only `placed → out_for_delivery → paid` orders
  // ever land here. Filter is a fast-find for cash-in-transit orders.
  { value: "out_for_delivery", label: "Out for delivery (COD)" },
  { value: "paid", label: "Paid" },
  { value: "fulfilled", label: "Fulfilled" },
  { value: "cancelled", label: "Cancelled" },
  { value: "partially_refunded", label: "Partially refunded" },
  { value: "refunded", label: "Refunded" },
];

/**
 * Truncate a UUID to its first 8 chars + ellipsis. The admin order summary
 * DTO has no order_number — `id` is the only stable identifier surfaced on
 * list views (P08-T13 carry-forward, plan §1995).
 */
function truncateId(id: string): string {
  return `${id.slice(0, 8)}…`;
}

/**
 * Render an ISO datetime as the London-local calendar date in YYYY-MM-DD
 * form, suitable for an `<input type="date">` `value`. We use Europe/London
 * because that's the server's "today" anchor (see DashboardService SQL);
 * keeping the list page on the same anchor is what makes the dashboard tile
 * → list deep-link round-trip honest.
 */
function isoToLondonDate(iso: string): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/London",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(iso));
}

/**
 * Convert a London-local date string (YYYY-MM-DD, as produced by an
 * `<input type="date">`) into an ISO UTC datetime at London-midnight on
 * that date. Mirrors the server's `date_trunc('day', now() AT TIME ZONE
 * 'Europe/London') AT TIME ZONE 'Europe/London'` cutoff.
 *
 * Offset is sampled at 12:00 UTC of the target date — a stable interior
 * point that falls on the same calendar day in both UTC and London for
 * every day of the year. On DST-transition Sundays (last Sun of March /
 * October) the offset can shift between midnight and noon; on those two
 * days a year this can be off by 1h. Acceptable tradeoff for V1.0.
 */
function londonDateToMidnightIso(date: string): string {
  const stablePoint = new Date(`${date}T12:00:00Z`);
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/London",
    hour: "2-digit",
    hour12: false,
  }).formatToParts(stablePoint);
  const londonHour = parseInt(
    parts.find((p) => p.type === "hour")?.value ?? "12",
    10,
  );
  const offsetMs = (londonHour - 12) * 60 * 60 * 1000;
  return new Date(
    new Date(`${date}T00:00:00Z`).getTime() - offsetMs,
  ).toISOString();
}

/**
 * Resolve a customer display name from the AdminOrderSummaryDTO. The wire
 * DTO has `customer_email` (nullable) + `customer_company_name` (nullable);
 * `<OrderRow>` expects a single `customerName`. We prefer the company name
 * for B2B legibility, fall back to email, then "(no email)".
 */
function customerLabel(o: AdminOrderSummaryDTO): string {
  return o.customer_company_name ?? o.customer_email ?? "(no email)";
}

export function OrdersList() {
  const { notify, notifyProblem } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState<AdminOrderSummaryDTO[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [density, setDensity] = useState<Density>("default");
  // Initial values seeded from the URL query string so deep-links from the
  // dashboard tiles (`/orders?status=paid&placed_after=…`) work — P08-T13
  // wiring preserved verbatim.
  const [statusFilter, setStatusFilter] = useState<OrderStatus | "all">(() => {
    const s = searchParams.get("status");
    return ORDER_STATUS_VALUES.includes(s as OrderStatus)
      ? (s as OrderStatus)
      : "all";
  });
  // Stored as a full ISO UTC datetime — preserves the dashboard's
  // London-midnight handoff verbatim. The date `<input>` displays the
  // London-local date derived from this ISO; user typing converts back to
  // a London-midnight ISO. This is what makes the dashboard tile's
  // "today" SUM agree with the orders this page lists.
  const [placedAfterIso, setPlacedAfterIso] = useState<string>(() => {
    const raw = searchParams.get("placed_after") ?? "";
    if (raw === "") return "";
    // Accept either a full ISO datetime or a bare YYYY-MM-DD date (legacy
    // bookmarks). For a bare date, interpret as London-local midnight.
    return raw.length === 10 ? londonDateToMidnightIso(raw) : raw;
  });
  const [q, setQ] = useState(searchParams.get("q") ?? "");
  const [debouncedQ, setDebouncedQ] = useState(q);
  const [loading, setLoading] = useState(false);

  // Density: read from localStorage on mount (Q5). AdminShell topbar
  // toggle persists the same key, so this hook picks up the latest
  // preference each time the page is visited.
  useEffect(() => {
    const stored = window.localStorage.getItem("wmart.density");
    if (stored === "compact" || stored === "default" || stored === "cosy") {
      setDensity(stored);
    }
  }, []);

  // Debounce the free-text query — mirrors CustomersList / CompaniesList.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 250);
    return () => clearTimeout(t);
  }, [q]);

  async function fetchPage(reset: boolean, cursor: string | null) {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("limit", "50");
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (debouncedQ.trim()) params.set("q", debouncedQ.trim());
      if (placedAfterIso) {
        params.set("placed_after", placedAfterIso);
      }
      if (cursor) params.set("cursor", cursor);
      const data = await api.get<AdminOrderListResponse>(
        `/v1/admin/orders?${params.toString()}`,
      );
      setItems((prev) => (reset ? data.items : [...prev, ...data.items]));
      setNextCursor(data.nextCursor);
    } catch (err) {
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
      else notify("error", "Failed to load orders");
    } finally {
      setLoading(false);
    }
  }

  // Re-fetch on filter/search change.
  useEffect(() => {
    void fetchPage(true, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, debouncedQ, placedAfterIso]);

  // Keep URL in sync with active filters so deep-links round-trip
  // (e.g. /orders?status=paid). Replace (not push) so the back button
  // doesn't accumulate history entries from typing in the search box.
  useEffect(() => {
    const next = new URLSearchParams();
    if (statusFilter !== "all") next.set("status", statusFilter);
    if (debouncedQ.trim()) next.set("q", debouncedQ.trim());
    if (placedAfterIso) next.set("placed_after", placedAfterIso);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, debouncedQ, placedAfterIso]);

  const hasAnyFilter =
    statusFilter !== "all" || debouncedQ.trim() !== "" || placedAfterIso !== "";

  // P17 — surface the Driver column whenever any visible row is COD. We
  // also force it on when the operator filters explicitly for
  // out_for_delivery (which IS the cash-in-transit slice), so the column
  // appears even before the first row arrives.
  const showDriverColumn = useMemo(
    () =>
      statusFilter === "out_for_delivery" ||
      items.some((o) => o.payment_processor === "cod"),
    [items, statusFilter],
  );

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-64">
          <Input
            type="search"
            label="Search"
            placeholder="Email or company…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <div className="w-48">
          <Select
            label="Status"
            value={statusFilter}
            onChange={(e) =>
              setStatusFilter(e.target.value as OrderStatus | "all")
            }
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </Select>
        </div>
        <div className="w-44">
          <Input
            type="date"
            label="Placed after"
            value={placedAfterIso ? isoToLondonDate(placedAfterIso) : ""}
            onChange={(e) =>
              setPlacedAfterIso(
                e.target.value ? londonDateToMidnightIso(e.target.value) : "",
              )
            }
          />
        </div>
        {hasAnyFilter ? (
          <Button
            variant="link"
            size="sm"
            onClick={() => {
              setStatusFilter("all");
              setQ("");
              setPlacedAfterIso("");
            }}
          >
            Clear
          </Button>
        ) : null}
      </div>

      {items.length === 0 && !loading ? (
        <EmptyState
          icon="package"
          title="No orders match"
          body="Adjust filters to see more results."
        />
      ) : (
        <>
          {/* P17 — show the Driver column whenever any visible row is a
              COD order. We keep the OrderRow primitive for non-COD rows
              and emit a wider 6-col TR for COD rows so the table layout
              stays consistent. The column header is hidden when no COD
              rows are visible (purely non-COD slices). */}
          {showDriverColumn ? (
            <Table density={density}>
              <THead>
                <TR>
                  <TH>Order</TH>
                  <TH>Customer</TH>
                  <TH>Placed</TH>
                  <TH className="text-right">Total</TH>
                  <TH>Status</TH>
                  <TH>Driver</TH>
                </TR>
              </THead>
              <TBody>
                {items.map((o) => (
                  <OrdersTableRow key={o.id} order={o} />
                ))}
              </TBody>
            </Table>
          ) : (
            <Table density={density}>
              <THead>
                <TR>
                  <TH>Order</TH>
                  <TH>Customer</TH>
                  <TH>Placed</TH>
                  <TH className="text-right">Total</TH>
                  <TH>Status</TH>
                </TR>
              </THead>
              <TBody>
                {items.map((o) => (
                  <OrderRow
                    key={o.id}
                    id={truncateId(o.id)}
                    customerName={customerLabel(o)}
                    customerHref={`/customers/${o.customer_id}`}
                    placedAt={o.placed_at}
                    totalMinor={o.total_minor}
                    status={o.status}
                    statusExtra={
                      o.supplier_pos_ready ? (
                        <StatusPill tone="success">POs ready</StatusPill>
                      ) : null
                    }
                    href={`/orders/${o.id}`}
                  />
                ))}
              </TBody>
            </Table>
          )}

          {nextCursor ? (
            <div className="flex justify-center">
              <Button
                variant="secondary"
                onClick={() => void fetchPage(false, nextCursor)}
                disabled={loading}
                loading={loading}
              >
                Load more
              </Button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

/**
 * Single OrdersList row when the Driver column is visible. Mirrors
 * `<OrderRow>` from `@wmart/ui` 1:1 for the first five cells (so styling
 * stays consistent with non-COD slices) and appends a 6th cell with the
 * driver attribution. Non-COD rows show an em-dash; COD rows show the
 * truncated driver_staff_id (no name yet — `/v1/admin/staff` endpoint
 * pending, see DispatchModal deviation note).
 */
function OrdersTableRow({ order }: { order: AdminOrderSummaryDTO }) {
  const isCod = order.payment_processor === "cod";
  return (
    <TR data-slot="order-row" data-status={order.status}>
      <TD className="font-mono text-body-sm">
        <a href={`/orders/${order.id}`} className="text-ink-900 hover:underline">
          {truncateId(order.id)}
        </a>
      </TD>
      <TD>
        <a
          href={`/customers/${order.customer_id}`}
          className="text-ink-700 hover:underline"
        >
          {customerLabel(order)}
        </a>
      </TD>
      <TD className="text-ink-500">
        {order.placed_at
          ? new Date(order.placed_at).toISOString().slice(0, 10)
          : "—"}
      </TD>
      <TD className="text-right">
        <Numeric serif>{formatGBP(order.total_minor)}</Numeric>
      </TD>
      <TD>
        <span className="inline-flex items-center gap-1.5">
          <StatusPill tone={orderStatusTone(order.status)}>
            {order.status.replace(/_/g, " ")}
          </StatusPill>
          {isCod ? <StatusPill tone="info">COD</StatusPill> : null}
          {order.supplier_pos_ready ? (
            <StatusPill tone="success">POs ready</StatusPill>
          ) : null}
        </span>
      </TD>
      <TD className="font-mono text-body-sm text-ink-500">
        {isCod && order.driver_staff_id
          ? `${order.driver_staff_id.slice(0, 8)}…`
          : "—"}
      </TD>
    </TR>
  );
}
